import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { fmtMdHm, fmtDateTime } from '../utils/time';
import ReportIncidentModal from '../components/ReportIncidentModal';
import { useAuth } from '../context/AuthContext';

const INCIDENT_API = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname.includes('pages.dev') ? 'https://guard-patrol-73go.onrender.com' : '');

export default function Incidents() {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  const loadIncidents = useCallback(() => {
    setLoading(true);
    axios.get('/api/incidents')
      .then(r => { setIncidents(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadIncidents();
  }, [loadIncidents]);

  const severityColor = {
    low: 'badge-info',
    medium: 'badge-warning',
    high: 'badge-danger',
    critical: 'badge-danger'
  };

  const getImgSrc = (inc) => {
    if (!inc) return null;
    if (inc.image_base64) {
      return inc.image_base64.startsWith('data:') ? inc.image_base64 : `data:image/jpeg;base64,${inc.image_base64}`;
    }
    if (inc.image_url) {
      if (inc.image_url.startsWith('http')) return inc.image_url;
      return `${INCIDENT_API}${inc.image_url}`;
    }
    return null;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">⚠️ Incidents</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
            {incidents.length} total · {incidents.filter(i => i.severity === 'critical').length} critical
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => setShowReportModal(true)}
            style={{
              padding: '9px 18px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #ef233c, #dc2626)',
              color: 'white', fontWeight: 800, fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 4px 12px rgba(239,35,60,0.3)',
            }}
          >
            ⚠️ Report Incident
          </button>
        </div>
      </div>

      <div className="incidents-split-grid">
        <div className="card">
          {loading ? <div>Loading...</div> : (
            <div className="table-scroll"><table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Guard</th>
                  <th>Severity</th>
                  <th>Time</th>
                  <th>Route / Source</th>
                  <th>Photo</th>
                </tr>
              </thead>
              <tbody>
                {incidents.map(inc => (
                  <tr key={inc.id} onClick={() => setSelected(inc)}
                    style={{ cursor: 'pointer', background: selected?.id === inc.id ? '#f0f4ff' : '' }}>
                    <td><strong>{inc.title}</strong><br />
                      <span style={{ fontSize: 12, color: '#666' }}>{inc.description?.slice(0, 60)}...</span>
                    </td>
                    <td>👮 {inc.guard_name || 'Guard'}</td>
                    <td><span className={`badge ${severityColor[inc.severity]}`}>{inc.severity}</span></td>
                    <td style={{ fontSize: 12 }}>{fmtMdHm(inc.reported_at)}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{inc.route_name || 'General Site'}</td>
                    <td>{(inc.image_url || inc.image_base64) ? '📸' : '—'}</td>
                  </tr>
                ))}
                {incidents.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '36px 16px' }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🛡️</div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>No incidents reported</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>Tap "Report Incident" above if you observe any security issue or hazard.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table></div>
          )}
        </div>

        {selected && (
          <div className="card" style={{ position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{selected.title}</div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            {getImgSrc(selected) && (
              <div
                style={{ cursor: 'zoom-in', position: 'relative' }}
                onClick={() => setPreviewPhoto(getImgSrc(selected))}
                title="Click to view full photo"
              >
                <img
                  src={getImgSrc(selected)}
                  alt="incident"
                  style={{ width: '100%', borderRadius: 8, marginBottom: 16, maxHeight: 300, objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            <div className="incidents-detail-meta">
              <div><span style={{ color: '#666' }}>Guard:</span> {selected.guard_name || 'Guard'}</div>
              <div><span style={{ color: '#666' }}>Severity:</span>
                <span className={`badge ${severityColor[selected.severity]}`} style={{ marginLeft: 6 }}>{selected.severity}</span>
              </div>
              <div><span style={{ color: '#666' }}>Time:</span> {fmtDateTime(selected.reported_at)}</div>
              {selected.route_name && (
                <div><span style={{ color: '#666' }}>Route:</span> {selected.route_name}</div>
              )}
              {selected.latitude && (
                <div><span style={{ color: '#666' }}>Location:</span> {parseFloat(selected.latitude).toFixed(5)}, {parseFloat(selected.longitude).toFixed(5)}</div>
              )}
            </div>

            {selected.description && (
              <div style={{ marginTop: 12, padding: 12, background: '#f8f9fa', borderRadius: 8, fontSize: 14 }}>
                {selected.description}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {previewPhoto && (
        <div
          onClick={() => setPreviewPhoto(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 16
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: 700, width: '100%', background: 'white', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px', background: '#0f172a', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700 }}>📸 Incident Evidence Photo</div>
              <button onClick={() => setPreviewPhoto(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ background: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center', maxHeight: '70vh' }}>
              <img src={previewPhoto} alt="Incident Full Evidence" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      )}

      {/* Report Incident Modal */}
      {showReportModal && (
        <ReportIncidentModal
          onClose={() => setShowReportModal(false)}
          onSuccess={loadIncidents}
        />
      )}
    </div>
  );
}
